// =========================
// TrendCard
// Renders `trends` + `marketScore` from the analyze-property response.
// trends:      { priceTrend, occupancyTrend, momentum, volatility, confidence }
// marketScore: { direction, score, confidence }
// =========================

import React from "react";
import { InfoTooltip } from "./InfoTooltip";

const COLORS = {
  up: "#16a34a",
  down: "#dc2626",
  flat: "#6b7280",
  text: "#111827",
  subtle: "#6b7280",
  warn: "#f59e0b",
};

function arrowFor(value) {
  if (typeof value !== "number" || !isFinite(value)) return { glyph: "→", color: COLORS.flat };
  if (value > 0.02) return { glyph: "▲", color: COLORS.up };
  if (value < -0.02) return { glyph: "▼", color: COLORS.down };
  return { glyph: "→", color: COLORS.flat };
}

function directionArrow(direction) {
  if (direction === "upward" || direction === "bullish") return { glyph: "▲", color: COLORS.up };
  if (direction === "downward" || direction === "bearish") return { glyph: "▼", color: COLORS.down };
  return { glyph: "→", color: COLORS.flat };
}

function fmtPct(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  if (Math.abs(n) <= 1.0001) return `${(n * 100).toFixed(1)}%`;
  return `${n.toFixed(1)}%`;
}

function fmtScore(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  if (Math.abs(n) <= 1.0001) return n.toFixed(2);
  return Math.round(n).toString();
}

// Price trend is noisy at the per-comp level (raw values can hit ±1.0+).
// Qualitative bucketing keeps the card honest without leaking spurious precision.
function priceTrendLabel(n) {
  if (typeof n !== "number" || !isFinite(n)) {
    return { text: "—", glyph: "→", color: COLORS.flat };
  }
  if (n > 0.10)  return { text: "Rising sharply",    glyph: "▲", color: COLORS.up };
  if (n > 0.02)  return { text: "Rising",            glyph: "▲", color: COLORS.up };
  if (n < -0.10) return { text: "Declining sharply", glyph: "▼", color: COLORS.down };
  if (n < -0.02) return { text: "Declining",         glyph: "▼", color: COLORS.down };
  return { text: "Stable", glyph: "→", color: COLORS.flat };
}

const MARKET_SCORE_TOOLTIP =
  "Market Score — Is the market heating up or cooling down? Direction " +
  "shows the recent trend. Score blends price, occupancy, and momentum: " +
  "-1 weakest, +1 strongest.";

function Row({ label, value, glyph, color }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 0", borderTop: "1px solid #f3f4f6",
    }}>
      <div style={{ fontSize: 13, color: COLORS.subtle }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ color, fontWeight: 700, fontSize: 16 }}>{glyph}</span>
        <span style={{ color: COLORS.text, fontWeight: 600, fontSize: 16 }}>{value}</span>
      </div>
    </div>
  );
}

// trends from the API is an array of per-comp objects; aggregate to a single
// signal before rendering. Also handles the legacy single-object shape.
function aggregateTrends(trendsInput) {
  if (!trendsInput) return null;
  if (!Array.isArray(trendsInput)) return trendsInput;
  if (trendsInput.length === 0) return null;

  const keys = ["priceTrend", "occupancyTrend", "momentum", "volatility", "confidence"];
  const out = {};
  for (const key of keys) {
    const vals = trendsInput.map(t => t[key]).filter(v => typeof v === "number");
    out[key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }
  return out;
}

function hasAnyTrendData(t) {
  if (!t) return false;
  const keys = ["priceTrend", "occupancyTrend", "momentum", "volatility", "confidence"];
  return keys.some((k) => typeof t[k] === "number" && t[k] !== 0);
}

export default function TrendCard({ trends, marketScore }) {
  const t = aggregateTrends(trends) || {};
  const m = marketScore || {};

  const priceLabel = priceTrendLabel(t.priceTrend);
  const occA = arrowFor(t.occupancyTrend);
  const momA = arrowFor(t.momentum);
  const dirA = directionArrow(m.direction);
  const hasData = hasAnyTrendData(t);

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>Market Trends</div>

      {hasData ? (
        <div style={{ marginTop: 8, paddingTop: 8 }}>
          <Row label="Price trend"      value={priceLabel.text}            glyph={priceLabel.glyph} color={priceLabel.color} />
          <Row label="Occupancy trend"  value={fmtPct(t.occupancyTrend)}  glyph={occA.glyph}   color={occA.color} />
          <Row label="Momentum"         value={fmtScore(t.momentum)}      glyph={momA.glyph}   color={momA.color} />
          <Row label="Volatility"       value={fmtPct(t.volatility)}      glyph={"≈"}          color={t.volatility > 0.5 ? COLORS.warn : COLORS.subtle} />
          <Row label="Trend confidence" value={fmtPct(t.confidence)}      glyph={"%"}          color={COLORS.subtle} />
        </div>
      ) : (
        <div style={{
          marginTop: 12, padding: "16px 14px", background: "#f9fafb",
          border: "1px dashed #e5e7eb", borderRadius: 8,
          fontSize: 13, color: COLORS.subtle, lineHeight: 1.5,
        }}>
          Trend signals populate after multiple snapshots accumulate. Re-run
          with <strong>Run market simulation</strong> checked a few times
          (or wait for live ingestion) to build the time-series the engine
          needs.
        </div>
      )}

      <div style={{ marginTop: 18, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div
            style={{
              fontSize: 12,
              color: COLORS.subtle,
              textTransform: "uppercase",
              letterSpacing: 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Market Score
            <InfoTooltip text={MARKET_SCORE_TOOLTIP} />
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ color: dirA.color, fontWeight: 800, fontSize: 18 }}>{dirA.glyph}</span>
            <span style={{ color: COLORS.text, fontWeight: 700, fontSize: 18, textTransform: "capitalize" }}>
              {m.direction || "flat"}
            </span>
          </div>
        </div>
        <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 13, color: COLORS.subtle }}>
          <span>Score: <strong style={{ color: COLORS.text }}>{fmtScore(m.score)}</strong></span>
          <span>Confidence: <strong style={{ color: COLORS.text }}>{fmtPct(m.confidence)}</strong></span>
        </div>
      </div>
    </div>
  );
}

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
};
const headerStyle = {
  fontSize: 12, color: COLORS.subtle, textTransform: "uppercase", letterSpacing: 1,
};
